import React, { useState } from 'react';
import { RebalanceReportColumn } from '../sharedTypes';

/**
 * Custom React hook for implementing column drag-and-drop functionality.
 *
 * @template T - The type of data in each row.
 * @param {RebalanceReportColumn<T>[]} initialColumns - An array of initial column definitions.
 * @returns {object} An object containing state and event handlers for drag-and-drop.
 */
const useColumnDragDrop = <T extends { [key: string]: any }>(initialColumns: RebalanceReportColumn<T>[]) => {
  const [columns, setColumns] = useState<RebalanceReportColumn<T>[]>(initialColumns);
  const [draggedColumn, setDraggedColumn] = useState<keyof T | null>(null);

  /**
   * Handles the drag start event for a column.
   *
   * @param {React.DragEvent<HTMLElement>} e - The drag event.
   * @param {keyof T} columnAccessor - The accessor key of the dragged column.
   */
  const handleDragStart = (e: React.DragEvent<HTMLElement>, columnAccessor: keyof T) => {
    setDraggedColumn(columnAccessor);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(columnAccessor));
  };

  /**
   * Handles the drag over event for a column.
   *
   * @param {React.DragEvent<HTMLElement>} e - The drag event.
   * @param {keyof T} targetAccessor - The accessor key of the target column.
   */
  const handleDragOver = (e: React.DragEvent<HTMLElement>, targetAccessor: keyof T) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  /**
   * Handles the drop event for a column.
   *
   * @param {React.DragEvent<HTMLElement>} e - The drop event.
   * @param {keyof T} targetAccessor - The accessor key of the target column.
   */
  const handleDrop = (e: React.DragEvent<HTMLElement>, targetAccessor: keyof T) => {
    e.preventDefault();
    if (!draggedColumn || draggedColumn === targetAccessor) {
      setDraggedColumn(null);
      return;
    }

    const newColumns = [...columns];
    const draggedIndex = newColumns.findIndex((col) => col.accessor === draggedColumn);
    const targetIndex = newColumns.findIndex((col) => col.accessor === targetAccessor);

    const [draggedItem] = newColumns.splice(draggedIndex, 1);
    newColumns.splice(targetIndex, 0, draggedItem);

    setColumns(newColumns);
    setDraggedColumn(null);
  };

  /**
   * Handles the drag end event.
   */
  const handleDragEnd = () => { setDraggedColumn(null); };

  return {
    columns,
    setColumns,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
    draggedColumn,
  };
};

export default useColumnDragDrop;
